import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetCurrentUserQuery,
  useVerifyMutation,
} from '../../store/services';
import { VerifyFormValues } from '../types';
import { clearMfa } from '../../store/slice';

const useVerifyTwoFactor = () => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setError,
  } = useForm<VerifyFormValues>();
  const [globalError, setGlobalError] = useState('');

  const totpCode = watch('totpToken');

  const { mfaToken, userId, totpNotActive } = useAppSelector(
    (state) => state.auth,
  );

  const [getCurrentUser] = useLazyGetCurrentUserQuery();

  const [verify, { isLoading, isSuccess }] = useVerifyMutation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const getCurrentUserAction = useCallback(() => {
    getCurrentUser();
  }, []);

  const onSubmit = handleSubmit(async (values) => {
    if (!values.totpToken) {
      setError('totpToken', {
        type: 'custom',
        message: 'Code is required',
      });
    } else {
      const response = (await verify({
        totpToken: values.totpToken,
        mfaToken,
        active: !totpNotActive,
      })) as {
        error: {
          data: { message: string; mfaNextToken?: string };
          status?: number;
        };
      };

      if (response?.error?.data) {
        if (response?.error?.status === 403) {
          setGlobalError('Access Denied');
        } else if (response.error.data.message) {
          setGlobalError(response.error.data.message);
        }
      } else {
        const redirectPath = localStorage.getItem('redirectPath');

        if (!redirectPath) {
          navigate('/');
        }

        setGlobalError('');
      }
    }
  });

  useEffect(() => {
    if (totpCode && totpCode.length === 6) {
      onSubmit();
    }
  }, [totpCode]);

  useEffect(() => {
    if (userId) {
      getCurrentUserAction();
    }
  }, [userId, getCurrentUserAction]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(clearMfa());
    }
  }, [isSuccess]);

  return {
    onSubmit,
    errors,
    control,
    isLoading,
    globalError,
  };
};

export default useVerifyTwoFactor;
